/**
 * LiveKitViewer · STEP 8-BUYER-LIVE-UI
 *
 * Consumer LIVE Viewer — video-first redesign.
 * Mobile: full-screen 세로 영상 · 댓글 Overlay · 하단 Mini 상품 카드 · 하단 댓글입력 + 🛍 상품
 * PC/Tablet: 좌측 영상 + 우측 실시간 채팅 패널 · 우측 세로 하트/채팅/상품 버튼 없음 (제거)
 *
 * REUSE (재개발 금지 · 백엔드 무변경):
 *   · LiveKit subscribe · viewer token · Data Channel (chat/social_proof/pin)
 *   · /public/streaming/sessions/{sid}/{live-context, chat}  · /products/{pid}
 *   · Rate Limit · Pin · Comment Engine · OMS · Cart · Checkout · Social Proof
 *
 * 제거 (사용자 요구):
 *   · 우측 세로 하트 · 채팅 · 상품 버튼
 *   · 하트 수/floating heart · 상단 큰 pinned card 배너
 *   · 방송상품 전체 카드 · 큰 채팅 카드 (bottom sheet 로 이동)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Radio, Volume2, VolumeX, Share2, MoreHorizontal, X,
  Send, ShoppingBag, Smile, Users, Pin, Play,
} from 'lucide-react';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import OptionSheet from '@/components/OptionSheet';
// STEP LIVE-SELF-OVERLAY-UI-1 · 자사라이브 상품 Overlay 공통 컴포넌트 REUSE.
//   · 영상 재인코딩/Egress 합성 없이 React UI 레이어에서만 표시.
//   · 플래그 OFF (기본) 시 미표시 → 기존 클릭형 상품 카드 UI 그대로 유지.
import LivePresetOverlay from '@/components/live/LivePresetOverlay';
import OverlayFreeBox from '@/components/live/OverlayFreeBox';
import { getRect, baseFrac, cardOrientation, isFluid } from '@/lib/overlayLayout';
import { OverlayTimerCard, sizeTableHasData, sizeTableDesc } from '@/components/live/OverlayExtras';
import { timerViewOf } from '@/lib/timerLocal';
import ProductVideoPlayer from '@/components/live/ProductVideoPlayer';

const _fmt = (s) => {
  s = Math.max(0, Math.floor(s));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
};
const _won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const CHAT_POLL_MS = 4000;
const CTX_POLL_MS = 4000;
const TOAST_TTL_MS = 3000;
const MAX_TOASTS = 3;
const OVERLAY_MSG_COUNT = 5;         // 영상 위 overlay 로 노출할 최근 댓글 수
const RIGHT_PANEL_MSG_COUNT = 200;   // 우측 패널 최대 보관

// ── Social Proof activity queue — STEP 8-I-4A 이후 미사용 (댓글 흐름으로 이동) ─
// (기존 훅은 다른 곳에서 import 되지 않으므로 참고용 stub 만 남기고 실제 로직 제거)
function useToastQueue() { return { toasts: [], push: () => {} }; }

// ── Chat sidebar (Desktop/Tablet right panel) ─────────────────────────────
// STEP 8-I-5A · LIVE Notice — compact chip (Mobile UX Micro Fix).
// 기본: 영상 top-left 작은 pill · Tap → 아래로 펼쳐 전체 내용 · 다시 Tap → 접힘.
// 신규 API 0 · /live-context.live_notice REUSE.
const LiveNoticeBar = ({ notice }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!notice?.text) return null;
  return (
    // C-UI-POSITION-FIX-1 · 모바일 우측 상단 (PC 는 기존 좌측 유지).
    <div className="absolute top-[52px] right-2 lg:right-auto lg:left-2 z-20 max-w-[75%] flex flex-col items-end lg:items-start"
         data-testid="livekit-viewer-notice-wrap">
      <button type="button" onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/75 backdrop-blur-md border border-white/70 shadow-md text-[11px] font-black text-[#17181C]"
              aria-expanded={expanded}
              data-testid="livekit-viewer-notice-bar">
        <span className="text-[12px]">📢</span>
        <span>공지</span>
      </button>
      {expanded && (
        <div className="mt-1.5 max-w-[280px] rounded-xl bg-white/75 backdrop-blur-md border border-white/70 shadow-lg p-2.5"
             data-testid="livekit-viewer-notice-expanded">
          <p className="text-[11px] font-black text-[#17181C] leading-relaxed whitespace-pre-line">
            {notice.text}
          </p>
          <p className="mt-1 text-right text-[9px] font-black text-[#FF4F3D]">접기 ▲</p>
        </div>
      )}
    </div>
  );
};

// LIVE-NOTICE-DESCRIPTION-UI-FIX-1 · 소개 중 상품 카드 위 중앙 '⌃ 상품설명' 접기·펼치기 (기존 size_table 데이터 재사용).
const DescBar = ({ product }) => {
  const [open, setOpen] = useState(false);
  const st = product?.size_table;
  if (!sizeTableHasData(st)) return null;
  const cols = Array.isArray(st.columns) ? st.columns : [];
  const rows = Array.isArray(st.rows) ? st.rows : [];
  const guide = (st.guide_text || '').toString().trim();
  return (
    // LIVE-DESCRIPTION-ATTACH-HIDE-FIX-1 · 탭은 카드 상단 중앙에 부착 · 설명은 카드 뒤에서 위로 슬라이드.
    <div data-testid="live-desc-bar-wrap" data-open={open ? '1' : '0'}>
      <div className="absolute left-2 right-2 bottom-[calc(100%-12px)] overflow-hidden z-0 pointer-events-none">
        <div className={`max-h-[42vh] overflow-y-auto overscroll-contain rounded-t-2xl bg-white/75 backdrop-blur-md border border-white/70 shadow-lg px-3 pt-3 pb-8 text-[#17181C] transition-[transform,opacity] duration-300 ease-out ${open ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-full opacity-0'}`}
             aria-hidden={!open} data-testid="live-desc-panel">
          <p className="text-[12px] font-black text-[#E5352B] mb-1.5 line-clamp-2">{product.name}</p>
          {cols.length > 0 && rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] border-collapse" data-testid="live-desc-table">
                <thead><tr>{cols.map((c, i) => <th key={i} className="px-1.5 py-1 font-black border-b border-black/10 whitespace-nowrap">{c}</th>)}</tr></thead>
                <tbody>{rows.map((r, ri) => (
                  <tr key={ri}>{cols.map((_, ci) => <td key={ci} className={`px-1.5 py-1 text-center border-b border-black/5 ${ci === 0 ? 'font-black' : 'font-semibold'}`}>{(r && r[ci]) || ''}</td>)}</tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {sizeTableDesc(st).map(([k, v]) => (
            <p key={k} className="mt-1.5 text-[12px] leading-snug flex gap-2" data-testid={`live-desc-${k === '소재' ? 'material' : 'etc'}`}>
              <span className="font-black text-[#E5352B] shrink-0">{k}</span>
              <span className="font-bold whitespace-pre-line break-words">{v}</span>
            </p>
          ))}
          {guide && <p className="mt-1.5 pt-1.5 border-t border-black/10 text-[11px] font-bold text-[#4A4B50] whitespace-pre-line">{guide}</p>}
        </div>
      </div>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full z-20 inline-flex items-center gap-1 px-4 pt-1 pb-0.5 rounded-t-xl bg-white/95 text-[11px] font-black text-[#17181C]"
              data-testid="live-desc-bar">
        <span className={`inline-block transition-transform ${open ? 'rotate-180' : ''}`}>⌃</span> 상품설명
      </button>
    </div>
  );
};

const ChatSidebar = ({ messages, viewerCount }) => {
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current; if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);
  return (
    <aside className="hidden lg:flex flex-col w-80 xl:w-96 shrink-0 border-l border-white/10 bg-[#0A0A0A] h-full min-h-0"
            data-testid="live-chat-sidebar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <p className="text-sm font-black text-white">실시간 채팅</p>
        <span className="text-xs text-white/60 inline-flex items-center gap-1">
          <Users size={11} /> {viewerCount != null ? viewerCount.toLocaleString('ko-KR') : '—'}
        </span>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2" data-testid="live-chat-sidebar-list">
        {messages.length === 0 && (
          <p className="text-xs text-white/40 text-center py-10">첫 댓글을 남겨보세요.</p>
        )}
        {messages.map((m) => (
          <div key={m.message_id} className="text-xs leading-snug break-words flex items-start gap-2"
                data-testid={`live-side-msg-${m.message_id}`}>
            <div className="w-6 h-6 rounded-full bg-white/10 shrink-0 flex items-center justify-center text-[10px] font-black text-white/80">
              {m.author_masked?.[0] || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-black text-white/90">
                {m.author_role === 'seller' && (
                  <span className="mr-1 px-1 py-0.5 rounded bg-[#FF3B30] text-white text-[9px] align-middle">판매자</span>
                )}
                {m.author_masked}
              </p>
              <p className="text-white/70 break-words">{m.text}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40">채팅은 2.5초마다 가능해요.</p>
    </aside>
  );
};

// ── Product Bottom Sheet (오늘 방송 상품) ─────────────────────────────────
// STEP 8-C · visibility mode 필터 · introduced_only 이면 is_introduced=true
// 상품만 노출 (미소개는 buyer 로부터 숨김). all 모드는 기존 동작 그대로.
const ProductBottomSheet = ({ open, onClose, products, activeId, visibilityMode, onPick, videoPids, onVideo }) => {
  if (!open) return null;
  const shown = (visibilityMode === 'introduced_only')
    ? (products || []).filter((p) => !!p.is_introduced || p.product_id === activeId)
    : (products || []);
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" data-testid="live-product-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full lg:max-w-md bg-white dark:bg-[#141414] rounded-t-2xl max-h-[80dvh] flex flex-col animate-in slide-in-from-bottom duration-200 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 dark:border-white/10">
          <p className="text-sm font-black text-black dark:text-white">
            오늘 방송 상품 <span className="text-gray-400 font-medium">({shown.length})</span>
            {visibilityMode === 'introduced_only' && (
              <span className="ml-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400" data-testid="live-product-sheet-introduced-only-badge">
                소개한 상품만
              </span>
            )}
          </p>
          <button type="button" onClick={onClose} className="p-1" data-testid="live-product-sheet-close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {shown.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-10" data-testid="live-product-sheet-empty">
              {visibilityMode === 'introduced_only'
                ? '판매자가 상품을 소개하면 여기에 표시돼요.'
                : '아직 등록된 상품이 없어요.'}
            </p>
          )}
          {shown.map((p) => {
            const isActive = p.product_id === activeId;
            const soldOut = p.stock_status === 'sold_out' || p.sale_status === 'closed';
            const hasVideo = !!videoPids?.has(p.product_id);
            return (
              <div key={p.product_id} className="flex items-center gap-1.5">
              <button type="button"
                      onClick={() => { if (!soldOut) onPick(p); }}
                      disabled={soldOut}
                      className="flex-1 min-w-0 flex items-center gap-3 p-2.5 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30] transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`live-sheet-product-${p.product_id}`}>
                <SafeImg src={p.image} kind="product" alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate text-black dark:text-white">{p.name}</p>
                  <p className="text-sm font-black text-[#FF3B30]">{_won(p.sale_price)}</p>
                  <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {isActive && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> 소개중
                      </span>
                    )}
                    {soldOut && (
                      <span className="px-1.5 py-0.5 rounded-full bg-gray-500 text-white text-[9px] font-black">품절</span>
                    )}
                  </div>
                </div>
                <ShoppingBag size={16} className="text-gray-400 shrink-0" />
              </button>
              {hasVideo && (
                <button type="button" onClick={() => onVideo(p)} aria-label={`${p.name} 영상 보기`}
                        className="shrink-0 self-stretch inline-flex flex-col items-center justify-center gap-0.5 px-2.5 rounded-xl border border-[#FF3B30]/30 text-[#FF3B30] text-[10px] font-black hover:bg-[#FF3B30]/5"
                        data-testid={`live-sheet-product-video-${p.product_id}`}>
                  <Play size={13} className="fill-current" /> 영상
                </button>
              )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Comment overlay (video 위 · 최근 N 개 · 페이드아웃) ─────────────────
// STEP 8-I-4A · Activity (author_role='activity' or message_type='activity') 은
// 아이콘+문구만 그대로 · 아바타·nickname·판매자 badge 없이 노출.
const CommentOverlay = ({ messages, pinned, mountTsMs }) => {
  const recent = messages.slice(-OVERLAY_MSG_COUNT);
  return (
    <div className="absolute left-2 right-14 sm:right-24 lg:right-6 pointer-events-none z-20 space-y-1"
          style={{ bottom: 'calc(var(--lv-bottom, 0px) + 8px)' }}
          data-testid="live-comment-overlay">
      {pinned && (
        <div className="inline-flex max-w-full items-start gap-1.5 px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm text-white text-[11px] leading-snug"
              data-testid="live-pinned-mini">
          <Pin size={11} className="text-[#FF3B30] shrink-0 mt-0.5" />
          <span className="font-black text-[#FF3B30] mr-1 shrink-0">고정</span>
          <span className="line-clamp-2 break-words">{pinned.text}</span>
        </div>
      )}
      {recent.map((m) => {
        const isActivity = m.author_role === 'activity' || m.message_type === 'activity' || m._activity;
        // STEP 8-I-4A · mountTsMs 이후 도착한 활동만 첫 등장 애니메이션 · 히스토리 로드는 정적.
        const arrivedMs = new Date(m.created_at || 0).getTime();
        const isFresh = mountTsMs && arrivedMs && arrivedMs >= mountTsMs;
        if (isActivity) {
          const ev = m.activity_event || 'order_created';
          const animCls = isFresh
            ? (ev === 'cart_add' ? 'lm-activity-bounce'
                : ev === 'checkout_enter' ? 'lm-activity-slide'
                : 'lm-activity-pop')
            : '';
          const emojiCls = isFresh ? 'lm-emoji-sparkle' : '';
          return (
            <div key={m.message_id} className="block">
              <div className={`inline-flex max-w-full items-center gap-2 px-3 py-1.5 rounded-lg bg-black/55 backdrop-blur-sm text-white text-[13px] font-black leading-snug ${animCls}`}
                    data-testid={`live-overlay-activity-${m.message_id}`}>
                <span className={`text-[26px] leading-none ${emojiCls}`}>🎉</span>
                <span className="break-words">{m.text.replace(/^🎉\s*/, '')}</span>
              </div>
            </div>
          );
        }
        return (
          <div key={m.message_id}
                className="inline-flex max-w-full items-start gap-1.5 px-2 py-1 rounded-lg bg-black/40 backdrop-blur-sm text-white text-[11px] leading-snug"
                data-testid={`live-overlay-msg-${m.message_id}`}>
            <div className="w-4 h-4 rounded-full bg-white/20 shrink-0 flex items-center justify-center text-[8px] font-black">
              {m.author_masked?.[0] || '?'}
            </div>
            <p className="break-words">
              {m.author_role === 'seller' && (
                <span className="mr-1 px-1 py-0.5 rounded bg-[#FF3B30] text-white text-[8px] align-middle">판매자</span>
              )}
              <span className="font-black mr-1">{m.author_masked}</span>
              <span className="text-white/90">{m.text}</span>
            </p>
          </div>
        );
      })}
    </div>
  );
};

// ── Mini product card (영상 하단 · active_product) ────────────────────────
const MiniProductCard = ({ product, onOpenSheet, hasVideo, onVideo, children }) => {
  if (!product) return null;
  const soldOut = product.stock_status === 'sold_out' || product.sale_status === 'closed';
  const buy = () => { if (!soldOut) onOpenSheet(product); };
  return (
    // SELF-LIVE-CONSUMER-OVERLAY-FIX-1 B-3 · 밝은 흰색 반투명 카드 · 상품명 최대 2줄 · 구매하기.
    <div className="relative px-2 pt-0 pb-0">
    {children}
    <div role="button" tabIndex={0} onClick={buy} onKeyDown={(e) => { if (e.key === 'Enter') buy(); }}
            aria-disabled={soldOut}
            className={`relative z-10 w-full flex items-center gap-2 px-2 py-1.5 rounded-2xl bg-white/95 text-gray-900 shadow-lg text-left transition-transform active:scale-[0.99] cursor-pointer ${soldOut ? 'opacity-60' : ''}`}
            data-testid="live-mini-product">
      <SafeImg src={product.image} kind="product" alt="" className="w-11 h-11 rounded-xl object-cover shrink-0" />
      <div className="flex-1 min-w-0 leading-tight">
        <p className="text-[11px] font-black text-[#FF3B30] inline-flex items-center gap-1 leading-tight">
          <span className="w-1 h-1 rounded-full bg-[#FF3B30] animate-pulse" /> 소개중
        </p>
        <p className="text-[13px] font-black leading-snug line-clamp-2 break-words" data-testid="live-mini-product-name">{product.name}</p>
        <p className="text-[13px] font-black leading-tight">{_won(product.sale_price)}</p>
      </div>
      {hasVideo && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onVideo(product); }}
                className="shrink-0 inline-flex items-center gap-1 px-2.5 min-h-[34px] rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-200 text-gray-800 text-[11px] font-bold"
                aria-label="상품 소개영상 보기" data-testid="live-mini-product-video">
          <Play size={11} className="fill-current" />
          <span className="hidden min-[360px]:inline">영상 보기</span>
          <span className="min-[360px]:hidden">영상</span>
        </button>
      )}
      <span className={`shrink-0 px-3.5 py-2 rounded-full text-[12px] font-black ${soldOut ? 'bg-gray-300 text-gray-600' : 'bg-[#FF3B30] text-white'}`}
            data-testid="live-mini-product-buy">
        {soldOut ? '품절' : '구매하기'}
      </span>
    </div>
    </div>
  );
};

// SELF-LIVE-CONSUMER-OVERLAY-FIX-1 B-1 · 좌측 상단 상품 목록 (모바일 · 세로 1열 · 약 5개 · 내부 스크롤).
const ProductRail = ({ products, activeId, visibilityMode, open, setOpen, onPick, videoPids, onVideo }) => {
  const shown = (visibilityMode === 'introduced_only')
    ? (products || []).filter((p) => !!p.is_introduced || p.product_id === activeId)
    : (products || []);
  if (!shown.length) return null;
  return (
    <div className="lg:hidden absolute top-[52px] left-2 z-30 flex flex-col items-start gap-1.5" data-testid="live-product-rail">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-black shadow-md"
              data-testid="live-product-rail-toggle">
        <ShoppingBag size={12} /> 상품 {shown.length}
      </button>
      {open && (
        <div className="flex flex-col gap-1.5 p-1.5 rounded-2xl bg-black/35 backdrop-blur-md max-h-[322px] overflow-y-auto overscroll-contain"
             data-testid="live-product-rail-list">
          {shown.map((p, i) => {
            const isActive = p.product_id === activeId;
            const soldOut = p.stock_status === 'sold_out' || p.sale_status === 'closed';
            const hasVideo = !!videoPids?.has(p.product_id);
            return (
              <div key={p.product_id} className="relative w-14 shrink-0 flex flex-col">
              <button type="button" disabled={soldOut}
                      onClick={() => { setOpen(false); onPick(p); }}
                      className={`relative w-14 h-14 shrink-0 rounded-xl overflow-hidden border-2 ${isActive ? 'border-[#FF3B30]' : 'border-white/40'} disabled:opacity-50`}
                      data-testid={`live-product-rail-item-${p.product_id}`} data-active={isActive ? '1' : '0'}>
                <SafeImg src={p.image} kind="product" alt="" className="w-full h-full object-cover" />
                <span className={`absolute top-0.5 left-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black flex items-center justify-center ${isActive ? 'bg-[#FF3B30] text-white' : 'bg-black/70 text-white'}`}>
                  {p.slot_number || i + 1}
                </span>
                {isActive && <span className="absolute bottom-0 inset-x-0 bg-[#FF3B30] text-white text-[9px] font-black text-center leading-[14px]">소개중</span>}
                {soldOut && <span className="absolute inset-0 bg-black/55 text-white text-[10px] font-black flex items-center justify-center">품절</span>}
              </button>
              {hasVideo && isActive && (
                <button type="button" onClick={() => onVideo(p)} aria-label={`${p.name} 영상 보기`}
                        className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white text-[#FF3B30] shadow-md flex items-center justify-center"
                        data-testid={`live-product-rail-video-${p.product_id}`}>
                  <Play size={11} className="fill-current ml-px" />
                </button>
              )}
              {hasVideo && !isActive && (
                <button type="button" onClick={() => onVideo(p)} aria-label={`${p.name} 영상 보기`}
                        className="mt-1 h-[22px] w-full rounded-full bg-white/90 text-[#FF3B30] text-[10px] font-black inline-flex items-center justify-center gap-0.5"
                        data-testid={`live-product-rail-video-${p.product_id}`}>
                  <Play size={9} className="fill-current" /> 영상
                </button>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────
const LiveKitViewer = () => {
  const { sessionId } = useParams();
  // LIVEMOA-LIVE-TEST-PREVIEW-INTEGRATION-1 · Studio 테스트 소비자 보기 (same-origin iframe · 데이터는 postMessage · API·LiveKit·주문·채팅 DB 미호출).
  const parentMode = typeof window !== 'undefined' && window.parent !== window
    && new URLSearchParams(window.location.search).get('source') === 'parent';
  // LIVE-SELLER-RECORDING-TEST-FLOW-1 · Preview 판매자 본인 TEST 방송 소비자 화면 (LIVE·채팅·주문 없음 · 실제 READY MP4).
  const testSchedule = (!parentMode && process.env.REACT_APP_STUDIO_TEST_ENTRY === '1' && typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('test_schedule') : null;
  const localMode = parentMode || !!testSchedule;
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const [videoPids, setVideoPids] = useState(() => new Set());
  const [pvItem, setPvItem] = useState(null);
  // LIVE-NOTICE-DESCRIPTION-FINAL-FIX-1 · 모바일 하단(상품설명·소개 카드·채팅)을 영상 위 투명 오버레이로 · 대댓글은 그 위로.
  const [bottomH, setBottomH] = useState(0);
  const bottomObs = useRef(null);
  const bottomRef = useCallback((el) => {
    if (bottomObs.current) { bottomObs.current.disconnect(); bottomObs.current = null; }
    if (!el) return;
    const upd = () => setBottomH(getComputedStyle(el).position === 'absolute' ? el.offsetHeight : 0);
    bottomObs.current = new ResizeObserver(upd);
    bottomObs.current.observe(el);
    upd();
  }, []);
  const audioRef = useRef(null);
  const roomRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const chatPollRef = useRef(null);
  const ctxPollRef = useRef(null);
  const lastChatTsRef = useRef(null);
  const seenChatIdsRef = useRef(new Set());
  const chatInputRef = useRef(null);

  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [ended, setEnded] = useState(false);
  const [connectState, setConnectState] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [context, setContext] = useState(null);
  const [chat, setChat] = useState([]);
  const [authed, setAuthed] = useState(null);
  const [productForSheet, setProductForSheet] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pinnedMessage, setPinnedMessage] = useState(null);
  const [chatText, setChatText] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [viewerCount, setViewerCount] = useState(null);
  const [publisherOffline, setPublisherOffline] = useState(false);   // STEP 8-LIVE-RECONNECT-GRACE
  // STEP 8-I-4A · Toast/Snackbar 스택 완전 제거 · Activity 는 댓글 흐름(chat) 안으로만.
  //   Activity 최초 등장 애니메이션은 mount 이후 도착 항목만 (히스토리 로드는 정적).
  const mountTsRef = useRef(Date.now());

  const activeProduct = context?.active_product || null;
  const liveProducts = context?.live_products || [];
  const [railOpen, setRailOpen] = useState(false);
  const [uiHidden, setUiHidden] = useState(false);

  const cleanup = useCallback(async () => {
    try { const r = roomRef.current; if (r) { try { await r.disconnect(); } catch {} } } catch {}
    roomRef.current = null;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (chatPollRef.current) { clearInterval(chatPollRef.current); chatPollRef.current = null; }
    if (ctxPollRef.current) { clearInterval(ctxPollRef.current); ctxPollRef.current = null; }
    setConnectState('disconnected');
  }, []);

  useEffect(() => {
    const onBefore = () => cleanup();
    window.addEventListener('beforeunload', onBefore);
    return () => { window.removeEventListener('beforeunload', onBefore); cleanup(); };
  }, [cleanup]);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me').then(() => { if (!cancelled) setAuthed(true); })
      .catch(() => { if (!cancelled) setAuthed(false); });
    return () => { cancelled = true; };
  }, []);
  // STEP 8-I-4A · 로컬 CustomEvent 리스너 제거 · checkout_enter 는 backend 경유로만.

  useEffect(() => {
    if (!sessionId) return;
    if (parentMode) { setMeta({ title: '테스트 소비자 보기 · 송출 없음', streaming_status: 'live' }); setConnectState('watching'); return; }
    let cancelled = false;
    if (testSchedule) {
      api.get(`/seller/broadcasts/${testSchedule}/test-viewer-context`).then(({ data }) => {
        if (cancelled) return;
        setMeta({ title: `TEST · ${data.title || '방송'}`, seller_name: data.seller_name });
        setContext(data);
        setVideoPids(new Set((data.videos || []).map((v) => v.product_id)));
        setConnectState('test');
      }).catch(() => { if (!cancelled) setError('테스트 화면을 열 수 없어요 · 방송 소유 판매자로 로그인해 주세요.'); });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data: m } = await api.get(`/public/streaming/sessions/${sessionId}`);
        if (cancelled) return;
        setMeta(m);
        if (m.streaming_status === 'ended') { setEnded(true); return; }
        if (m.started_at) {
          const startedMs = new Date(m.started_at).getTime();
          const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
          tick();
          timerRef.current = setInterval(tick, 1000);
        }
        setConnectState('connecting');
        const { data: tok } = await api.post(`/public/streaming/sessions/${sessionId}/viewer-token`, {});
        if (cancelled) return;
        const room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;
        room.on(RoomEvent.Reconnecting, () => setConnectState('reconnecting'));
        room.on(RoomEvent.Reconnected, () => setConnectState('watching'));
        room.on(RoomEvent.Disconnected, () => setConnectState('disconnected'));
        room.on(RoomEvent.ConnectionStateChanged, (st) => {
          if (st === ConnectionState.Disconnected) setConnectState('disconnected');
        });
        const updateViewers = () => setViewerCount(room.remoteParticipants.size + 1);
        room.on(RoomEvent.ParticipantDisconnected, (p) => {
          updateViewers();
          // STEP 8-LIVE-RECONNECT-GRACE · publisher 가 잠깐 이탈해도 즉시 종료 X.
          // "곧 방송이 다시 시작됩니다" overlay 표시 후 재접속 대기.
          if ((p?.identity || '').startsWith('seller_')) {
            setPublisherOffline(true);
          }
        });
        room.on(RoomEvent.ParticipantConnected, (p) => {
          updateViewers();
          if ((p?.identity || '').startsWith('seller_')) {
            setPublisherOffline(false);   // 재접속 감지 · overlay 해제
          }
        });
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind === Track.Kind.Video && videoRef.current) {
            try { track.attach(videoRef.current); } catch {}
          } else if (track.kind === Track.Kind.Audio && audioRef.current) {
            try { track.attach(audioRef.current); } catch {}
          }
        });
        room.on(RoomEvent.DataReceived, (payload, _p, _k, topic) => {
          try {
            const msg = JSON.parse(new TextDecoder().decode(payload));
            if (topic === 'social_proof' || msg?.type === 'social_proof') {
              // STEP 8-I-4A · social_proof 는 backend 가 이미 live_chat_messages 에도
              // 기록함 → chat state 에 activity 로 즉시 push (id=proof_id · 폴링과 dedupe 일치).
              const pid = msg.proof_id;
              if (!pid || seenChatIdsRef.current.has(pid)) return;
              seenChatIdsRef.current.add(pid);
              const iso = msg.ts ? new Date(msg.ts).toISOString() : new Date().toISOString();
              setChat((prev) => [...prev, {
                message_id: pid,
                author_role: 'activity',
                message_type: 'activity',
                activity_event: msg.event || 'order_created',
                text: msg.text,
                author_masked: msg.masked_name || '',
                created_at: iso,
              }].slice(-RIGHT_PANEL_MSG_COUNT));
              return;
            }
            if (topic === 'pin' || msg?.type === 'pin') {
              setPinnedMessage(msg.pinned_message || null); return;
            }
            if (topic === 'chat' || msg?.type === 'chat') {
              const id = msg.message_id;
              if (!id || seenChatIdsRef.current.has(id)) return;
              seenChatIdsRef.current.add(id);
              const iso = msg.ts ? new Date(msg.ts).toISOString() : new Date().toISOString();
              lastChatTsRef.current = iso;
              setChat((prev) => [...prev, {
                message_id: id, author_masked: msg.author_masked,
                author_role: msg.author_role || 'viewer', text: msg.text, created_at: iso,
              }].slice(-RIGHT_PANEL_MSG_COUNT));
            }
          } catch {}
        });
        await room.connect(tok.url, tok.token);
        setConnectState('watching');
        updateViewers();
        room.remoteParticipants.forEach((p) => {
          p.trackPublications.forEach((pub) => {
            if (pub.isSubscribed && pub.track) {
              if (pub.track.kind === Track.Kind.Video && videoRef.current) { try { pub.track.attach(videoRef.current); } catch {} }
              else if (pub.track.kind === Track.Kind.Audio && audioRef.current) { try { pub.track.attach(audioRef.current); } catch {} }
            }
          });
        });
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 404) setNotFound(true);
        else if (status === 403) setEnded(true);
        else setError(e?.response?.data?.detail || e?.message || String(e));
        setConnectState('disconnected');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, cleanup]);

  useEffect(() => {
    if (!sessionId || ended || testSchedule) return;
    if (parentMode) {
      const on = (e) => {
        if (e.origin !== window.location.origin || e.source !== window.parent || e.data?.type !== 'livemoa-preview-ctx') return;
        setContext({ ...(e.data.ctx || {}), streaming_status: 'live' });
      };
      window.addEventListener('message', on);
      window.parent.postMessage({ type: 'livemoa-preview-ready' }, window.location.origin);
      return () => window.removeEventListener('message', on);
    }
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/streaming/sessions/${sessionId}/live-context`);
        if (cancelled) return;
        setContext(data);
        const serverPin = data.pinned_message || null;
        setPinnedMessage((prev) => {
          if (!prev && !serverPin) return prev;
          if (!prev && serverPin) return serverPin;
          if (prev && !serverPin) return null;
          if (prev.message_id === serverPin?.message_id) return prev;
          return serverPin;
        });
      } catch {}
    };
    load();
    ctxPollRef.current = setInterval(load, CTX_POLL_MS);
    return () => { cancelled = true; if (ctxPollRef.current) clearInterval(ctxPollRef.current); };
  }, [sessionId, ended]);

  useEffect(() => {
    if (!sessionId || ended || localMode) return;
    let cancelled = false;
    const load = async () => {
      try {
        const params = lastChatTsRef.current ? { since: lastChatTsRef.current } : { limit: 50 };
        const { data } = await api.get(`/public/streaming/sessions/${sessionId}/chat`, { params });
        if (cancelled) return;
        const incoming = (data?.messages || []).filter((m) => !seenChatIdsRef.current.has(m.message_id));
        incoming.forEach((m) => seenChatIdsRef.current.add(m.message_id));
        if (incoming.length) {
          setChat((prev) => [...prev, ...incoming].slice(-RIGHT_PANEL_MSG_COUNT));
          const last = incoming[incoming.length - 1];
          if (last?.created_at) lastChatTsRef.current = last.created_at;
        }
      } catch {}
    };
    load();
    chatPollRef.current = setInterval(load, CHAT_POLL_MS);
    return () => { cancelled = true; if (chatPollRef.current) clearInterval(chatPollRef.current); };
  }, [sessionId, ended]);

  const sendChat = async () => {
    const t = chatText.trim();
    if (!t) return;
    if (localMode) {
      setChat((prev) => [...prev, { message_id: `test_${Date.now()}`, author_masked: '테스트', author_role: 'viewer', text: t,
        created_at: new Date().toISOString() }].slice(-RIGHT_PANEL_MSG_COUNT));
      setChatText('');
      return;
    }
    if (authed === false) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      navigate(`/login?return_url=${returnUrl}`);
      return;
    }
    if (chatBusy) return;
    setChatBusy(true);
    try {
      const { data } = await api.post(`/public/streaming/sessions/${sessionId}/chat`, { text: t });
      if (data?.message_id && !seenChatIdsRef.current.has(data.message_id)) {
        seenChatIdsRef.current.add(data.message_id);
        setChat((prev) => [...prev, {
          message_id: data.message_id, author_masked: data.author_masked,
          author_role: data.author_role || 'viewer', text: data.text, created_at: data.created_at,
        }].slice(-RIGHT_PANEL_MSG_COUNT));
        lastChatTsRef.current = data.created_at;
      }
      setChatText('');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        const returnUrl = encodeURIComponent(window.location.pathname);
        navigate(`/login?return_url=${returnUrl}`);
      } else if (status === 429) {
        toast.error(e?.response?.data?.detail || '너무 빠르게 작성하고 있어요.');
      } else if (status === 400) {
        toast.error(e?.response?.data?.detail || '전송 실패');
      }
    } finally { setChatBusy(false); }
  };

  const openOptionSheet = useCallback(async (product) => {
    if (localMode) { toast.info(`테스트 보기 · ${product?.name || '상품'} 구매 연결 · 주문·결제는 생성되지 않아요.`, { id: 'lv-test-buy' }); return; }
    try {
      const { data } = await api.get(`/products/${product.product_id}`);
      setProductForSheet({ ...data, broadcast_id: sessionId });
    } catch { setProductForSheet({ ...product, broadcast_id: sessionId }); }
  }, [sessionId]);

  const toggleMute = () => { setMuted((m) => !m); };

  // LIVE-CONSUMER-PRODUCT-VIDEO-1 · 소개영상 재생 중 이 시청자의 LIVE 소리만 임시 음소거 (사용자 설정 muted 는 보존).
  const liveMuted = muted || !!pvItem;
  useEffect(() => {
    [audioRef.current, videoRef.current].forEach((el) => { if (el) el.muted = liveMuted; });
  }, [liveMuted]);

  const activePid = context?.active_product?.product_id;
  useEffect(() => {
    if (!sessionId || testSchedule) return;
    api.get(`/public/streaming/sessions/${sessionId}/product-videos`)
      .then(({ data }) => setVideoPids(new Set((data?.videos || []).map((v) => v.product_id))))
      .catch(() => setVideoPids(new Set()));
  }, [sessionId, activePid]);

  const openVideo = useCallback(async (product) => {
    try {
      const { data } = await api.get(testSchedule
        ? `/seller/broadcasts/${testSchedule}/test-product-videos/${product.product_id}/play-url`
        : `/public/streaming/sessions/${sessionId}/product-videos/${product.product_id}/play-url`);
      setPvItem({ product, url: data.url, duration_sec: data.duration_sec, key: `${product.product_id}:${Date.now()}` });
    } catch {
      toast.error('소개영상을 불러오지 못했어요.');
    }
  }, [sessionId, testSchedule]);
  // TEST 화면 · 상품 목록에서 고른 상품을 로컬에서만 소개중으로 전환 (서버 변경 없음).
  const pickProduct = testSchedule
    ? (p) => setContext((c) => ({ ...(c || {}), active_product: p }))
    : openOptionSheet;

  const doShare = async () => {
    try {
      if (navigator.share) await navigator.share({ title: meta?.title || 'LIVEMOA LIVE', url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success('링크를 복사했어요.'); }
    } catch {}
  };

  const closeToHome = () => navigate('/');

  if (notFound) {
    return (
      <div className="fixed inset-0 z-50 bg-black text-white flex flex-col items-center justify-center gap-3 p-6 text-center"
            data-testid="livekit-viewer-notfound">
        <p className="text-xl font-black">방송을 찾을 수 없습니다</p>
        <Link to="/" className="px-4 py-2 rounded-full bg-white text-black text-xs font-bold">홈으로</Link>
      </div>
    );
  }

  return (
    <div ref={containerRef}
          className="fixed top-0 left-0 right-0 h-[100dvh] z-50 bg-black text-white flex flex-col lg:flex-row overflow-hidden"
          data-testid="livekit-viewer-page"
          style={{ height: '100dvh' }}>
      {/* Video column */}
      <div className="relative flex-1 min-w-0 flex flex-col min-h-0">
        {/* Top bar (over video) */}
        <div className="relative">
          <div className="absolute top-0 inset-x-0 z-30 p-3 flex items-center justify-between gap-2 bg-gradient-to-b from-black/70 to-transparent"
                data-testid="livekit-viewer-topbar">
            <div className="flex items-center gap-2 min-w-0">
              {meta?.seller_photo && (
                <img src={meta.seller_photo} alt=""
                      className="w-8 h-8 rounded-full object-cover shrink-0 border border-white/20"
                      onError={(e) => (e.target.style.display = 'none')} />
              )}
              <div className="min-w-0 leading-tight">
                <p className="text-xs font-black truncate" data-testid="livekit-viewer-title">
                  {meta?.title || meta?.seller_name || 'LIVEMOA LIVE'}
                </p>
                {meta?.seller_name && (
                  <Link to={`/seller/${meta.seller_id}`} className="text-[10px] text-white/70 hover:underline">
                    {meta.seller_name}
                  </Link>
                )}
              </div>
              {!ended && connectState === 'watching' && (
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30] text-[10px] font-black shrink-0"
                       data-testid="livekit-viewer-live-badge">
                  <Radio size={9} /> LIVE
                </span>
              )}
              {!ended && connectState === 'watching' && (
                <span className="text-[10px] text-white/80 font-bold shrink-0">{_fmt(elapsed)}</span>
              )}
              {viewerCount != null && (
                <span className="text-[10px] text-white/80 font-bold shrink-0 inline-flex items-center gap-0.5">
                  <Users size={10} /> {viewerCount.toLocaleString('ko-KR')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={toggleMute}
                      className="p-1.5 rounded-full bg-black/40 backdrop-blur hover:bg-black/60"
                      aria-label={muted ? '소리 켜기' : '음소거'} data-testid="livekit-viewer-mute">
                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
              <button type="button" onClick={doShare}
                      className="p-1.5 rounded-full bg-black/40 backdrop-blur hover:bg-black/60"
                      aria-label="공유" data-testid="livekit-viewer-share">
                <Share2 size={14} />
              </button>
              <button type="button" onClick={() => toast.info('더보기는 준비 중이에요.')}
                      className="p-1.5 rounded-full bg-black/40 backdrop-blur hover:bg-black/60 hidden sm:inline-flex"
                      aria-label="더보기" data-testid="livekit-viewer-more">
                <MoreHorizontal size={14} />
              </button>
              <button type="button" onClick={closeToHome}
                      className="p-1.5 rounded-full bg-black/40 backdrop-blur hover:bg-black/60"
                      aria-label="닫기" data-testid="livekit-viewer-close">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Video area */}
        {/* B-5 · 영상(배경) 터치 시 부가 UI 숨김·복원 (공지·채팅·상품 목록 조작은 제외). */}
        <div ref={stageRef} className="relative flex-1 min-h-0 bg-black flex items-center justify-center overflow-hidden"
             onClick={(e) => { if (e.target === e.currentTarget || e.target === videoRef.current) { setRailOpen(false); setUiHidden((v) => !v); } }}
             style={{ '--lv-bottom': `${bottomH}px` }}
             data-testid="livekit-viewer-stage" data-ui-hidden={uiHidden ? '1' : '0'}>
          <video ref={videoRef} playsInline autoPlay muted={liveMuted}
                  className="w-full h-full object-cover lg:object-contain bg-black"
                  data-testid="livekit-viewer-video" />
          <audio ref={audioRef} autoPlay muted={liveMuted} data-testid="livekit-viewer-audio" />
          {pvItem && (
            <ProductVideoPlayer key={pvItem.key} item={pvItem} boundsRef={stageRef}
                                onClose={() => setPvItem(null)} onBuy={openOptionSheet} />
          )}

          {/* STEP 8-I-5A · LIVE Notice bar (Buyer expand/collapse) — 위치: 영상 상단 (top bar 아래).
              공지 없으면 렌더 skip. Tap → inline expand. */}
          {!uiHidden && <LiveNoticeBar notice={context?.live_notice} />}
          {!uiHidden && (
            <ProductRail products={liveProducts} activeId={activeProduct?.product_id}
                         visibilityMode={context?.product_visibility_mode || 'all'}
                         open={railOpen} setOpen={setRailOpen} onPick={pickProduct}
                         videoPids={videoPids} onVideo={openVideo} />
          )}

          {/* STEP 8-LIVE-RECONNECT-GRACE · publisher 일시 이탈 overlay */}
          {publisherOffline && !ended && (
            <div className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-center px-6"
                  data-testid="livekit-viewer-publisher-offline">
              <div className="w-12 h-12 rounded-full border-4 border-white/30 border-t-white animate-spin" />
              <p className="text-base sm:text-lg font-black">곧 방송이 다시 시작됩니다</p>
              <p className="text-xs text-white/70 max-w-[280px] leading-relaxed">
                방송 연결을 다시 준비하고 있어요. 잠시만 기다려 주세요.
              </p>
              <button type="button" onClick={() => window.location.reload()}
                      className="mt-1 px-4 py-1.5 rounded-full bg-white text-black text-xs font-black hover:bg-white/90"
                      data-testid="livekit-viewer-publisher-offline-refresh">
                새로고침
              </button>
            </div>
          )}

          {/* Comment Overlay (over video) · 활동 아이템도 이 흐름 안에서 표시 */}
          {!uiHidden && <CommentOverlay messages={chat} pinned={pinnedMessage} mountTsMs={mountTsRef.current} />}

          {/* STEP 8-I-4A · 별도 Social Proof Stack 제거 · Activity 는 댓글 흐름 안으로만. */}

          {/* Loading / status overlays */}
          {connectState === 'test' && !error && (
            <div className="absolute inset-0 z-10 pointer-events-none flex flex-col items-center justify-center gap-1 text-center px-6"
                 data-testid="livekit-viewer-test-placeholder">
              <span className="px-2 py-0.5 rounded-full bg-amber-400 text-black text-[10px] font-black">TEST · 실제 LIVE 아님</span>
              <p className="text-sm font-black text-white/80">LIVE 영상 연결 대기</p>
              <p className="text-[11px] text-white/50">저장된 상품 소개영상 재생만 테스트할 수 있어요 · 주문·채팅 없음</p>
            </div>
          )}
          {(ended || (connectState !== 'watching' && connectState !== 'test' && !error)) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-center px-4 z-40"
                  data-testid="livekit-viewer-status">
              {ended ? (
                <>
                  <X size={40} className="text-white/60" />
                  <p className="font-black">방송이 종료되었습니다.</p>
                  <Link to="/" className="mt-3 px-4 py-2 rounded-full bg-white text-black text-xs font-bold">홈으로</Link>
                </>
              ) : (
                <>
                  <div className="w-8 h-8 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <p className="text-xs text-white/70">
                    {connectState === 'connecting' && '연결 중…'}
                    {connectState === 'reconnecting' && '재연결 중…'}
                    {connectState === 'idle' && '준비 중…'}
                  </p>
                </>
              )}
            </div>
          )}
          {error && !ended && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-center px-4 z-40"
                  data-testid="livekit-viewer-error">
              <p className="font-black text-[#FF3B30]">방송 접속 실패</p>
              <p className="text-[11px] text-white/70">{error}</p>
            </div>
          )}
          {/* STEP LIVE-SELF-OVERLAY-UI-1 · §2 자사라이브 소비자 영상 Overlay.
              · live-context.self_live_overlay_enabled === true 일 때만 표시.
              · active_product_display.preset / position 사용 (서버 SoT) → 상품/프리셋
                변경 시 자동 반영 (CTX_POLL_MS=4초 polling 재사용 · 신규 polling 0).
              · overlay_hidden / 종료 상태에서는 LivePresetOverlay 가 자체 노-op.
              · 영상 재인코딩 X · Egress 합성 X · React UI 레이어 only. */}
          {context?.self_live_overlay_enabled
            && context?.streaming_status === 'live'
            && !ended
            && activeProduct && (() => {
              // STEP STUDIO-OVERLAY-DRAG-RESIZE-1 · 저장된 자유 배치가 있으면 상품 카드만 반영 (QR·사이즈표 신규 추가 없음).
              const _lay = context?.overlay_layout;
              const _r = getRect(_lay, '9:16', 'card');
              const _common = {
                preset: context?.active_product_display?.preset || 'default',
                hidden: context?.active_product_display?.mode === 'hidden',
                product: activeProduct,
              };
              if (_r) {
                return (
                  <OverlayFreeBox rect={_r} base={baseFrac('9:16', 'card', _lay)} fluid={isFluid('card', _lay)} testid="livekit-viewer-free-card">
                    <LivePresetOverlay {..._common} free orientation={cardOrientation(_lay)} />
                  </OverlayFreeBox>
                );
              }
              return (
                <LivePresetOverlay {..._common}
                  position={context?.active_product_display?.position || 'bottom'} />
              );
            })()}
          {/* STUDIO-OVERLAY-UI-CONSOLIDATION-1 · 자사라이브 상품설명 (선택 · self_live_desc_enabled + 판매자 표시 ON 일 때만). */}
          {/* LIVE-NOTICE-DESCRIPTION-UI-FIX-1 · 중앙 상품설명 Overlay 대신 카드 위 접기·펼치기 바 (상품 변경 시 key 로 초기화). */}
          {/* SELF-LIVE-CONSUMER-OVERLAY-FIX-1 · 자사라이브 타이머 (기존 live-context.timer · 4초 polling 재사용 · 00:00/숨김 시 OverlayTimerCard 자체 null). */}
          {context?.streaming_status === 'live' && !ended && timerViewOf(context?.timer, Date.now()) && (
            <OverlayFreeBox rect={getRect(context?.overlay_layout, '9:16', 'timer') || { cx: 0.67, cy: 0.4, w: 0.3 }}
                            base={baseFrac('9:16', 'timer', context?.overlay_layout)} fluid testid="livekit-viewer-timer">
              <OverlayTimerCard free isPortrait viewOf={timerViewOf} timer={context.timer} testidPrefix="livekit-viewer" />
            </OverlayFreeBox>
          )}
        </div>

        <div ref={bottomRef} className="absolute lg:static bottom-0 inset-x-0 z-30 lg:bg-black" data-testid="live-bottom-overlay">
        {/* Mini product card (active_product) + 상단 부착 상품설명 탭 */}
        {activeProduct && (
          <MiniProductCard product={activeProduct} onOpenSheet={openOptionSheet}
                           hasVideo={videoPids.has(activeProduct.product_id)} onVideo={openVideo}>
            {context?.streaming_status === 'live' && !ended && (
              <DescBar key={activeProduct.product_id} product={activeProduct} />
            )}
          </MiniProductCard>
        )}

        {/* Chat input + 🛍 상품 (very bottom) */}
        <div className="px-2 pt-1.5 lg:pt-2 flex items-center gap-2 pb-[max(env(safe-area-inset-bottom),0.5rem)]"
              data-testid="live-bottom-bar">
          <div className={`flex-1 flex items-center gap-1 px-3 py-2 rounded-full bg-white/[0.08] border border-white/10 ${testSchedule ? 'invisible' : ''}`}>
            <input ref={chatInputRef} type="text" value={chatText} maxLength={200}
                    onChange={(e) => setChatText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendChat(); } }}
                    placeholder={authed === false ? '로그인 후 댓글 작성' : '댓글을 입력하세요…'}
                    className="flex-1 bg-transparent text-xs text-white placeholder-white/40 focus:outline-none"
                    data-testid="live-chat-input" />
            <button type="button" onClick={() => toast.info('이모지는 준비 중이에요.')}
                    className="p-1 text-white/50 hover:text-white hidden sm:block"
                    data-testid="live-chat-emoji">
              <Smile size={14} />
            </button>
            <button type="button" onClick={sendChat}
                    disabled={chatBusy || !chatText.trim()}
                    className="p-1 text-white/80 hover:text-[#FF3B30] disabled:opacity-40"
                    data-testid="live-chat-send">
              <Send size={14} />
            </button>
          </div>
          <button type="button" onClick={() => setSheetOpen(true)}
                  className="hidden lg:inline-flex shrink-0 px-4 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black items-center gap-1.5"
                  data-testid="live-open-products">
            <ShoppingBag size={14} /> 상품
          </button>
        </div>
        </div>
      </div>

      {/* Right chat sidebar (Desktop/large tablet) */}
      {!testSchedule && <ChatSidebar messages={chat} viewerCount={viewerCount} mountTsMs={mountTsRef.current} />}

      {/* Product Bottom Sheet */}
      <ProductBottomSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        products={liveProducts}
        videoPids={videoPids}
        onVideo={(p) => { setSheetOpen(false); openVideo(p); }}
        activeId={activeProduct?.product_id}
        visibilityMode={context?.product_visibility_mode || 'all'}
        onPick={(p) => { setSheetOpen(false); pickProduct(p); }}
      />

      {/* Option Sheet (기존 컴포넌트 재사용) */}
      <OptionSheet
        product={productForSheet}
        open={!!productForSheet}
        intent="buy"
        onClose={() => setProductForSheet(null)}
      />
    </div>
  );
};

export default LiveKitViewer;
